import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { IToolPanelParams } from "@ag-grid-community/core";
import Radio from "antd/es/radio";
import Select from "antd/es/select";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { EntityType, EntityTypeSupportingGroups, Group } from "@mapmycustomers/shared/types/entity";
import { FilterCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import useRefState from "@mapmycustomers/shared/util/hook/useRefState";
import useRerenderer from "@mapmycustomers/shared/util/hook/useRerenderer";
import { Button } from "@mapmycustomers/ui";

import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import useView from "@app/component/view/utils/useView";
import companyTableAnalytics from "@app/scene/company/companyTableAnalytics";
import peopleTableAnalytics from "@app/scene/people/peopleTableAnalytics";
import { getGroupsForEntity } from "@app/store/groups";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./GroupsFilterPanel.module.scss";

interface Props extends IToolPanelParams {
  analyticIssuer?: AnalyticsService;
  entityType: EntityTypeSupportingGroups;
  fieldName?: string;
  groups: Group[];
}

// this panel is almost a copy of the GroupsFilter component
const GroupsFilterPanel: React.FC<Props> = ({
  analyticIssuer,
  api,
  entityType,
  fieldName = "groups",
  groups,
}) => {
  const intl = useIntl();

  const forceRender = useRerenderer();
  const [inAll, setInAll] = useRefState(false, forceRender);
  const [selectedGroupIds, setSelectedGroupIds] = useRefState<Set<Group["id"]>>(
    new Set(),
    forceRender
  );

  const { updateFilterModel } = useView();

  // listen to filter changes to keep state in sync. Grid filter model takes precedence.
  useEffect(() => {
    const listener = () => {
      const model = api.getFilterModel();
      if (model[fieldName]) {
        const groups = model[fieldName];
        setInAll(groups.operator === FilterOperator.IN_ALL);
        setSelectedGroupIds(new Set(groups.value));
      } else {
        setSelectedGroupIds(new Set());
      }
    };
    api.addEventListener("filterChanged", listener);
    return () => api.removeEventListener("filterChanged", listener);
  }, [api, fieldName, setInAll, setSelectedGroupIds]);

  const groupOptions = useMemo(() => {
    return [...groups]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((group) => ({
        label: group.name,
        value: group.id,
      }));
  }, [groups]);

  const selectValues = useMemo(
    () => Array.from(selectedGroupIds.current),
    // disable next line because we need to listen to changes inside the ref, not the ref itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedGroupIds.current]
  );

  const handleGroupSelectionChange = useCallback(
    (checkedValues: Array<Group["id"]>) => {
      setSelectedGroupIds(new Set(checkedValues));
    },
    [setSelectedGroupIds]
  );

  const handleFilterStrategyChange = useCallback(
    (event) => {
      setInAll(event.target.value);
      analyticIssuer?.clicked([event.target.value ? "Filter All Selected" : "Filter Any Selected"]);
    },
    [analyticIssuer, setInAll]
  );

  const handleApplyClick = useCallback(() => {
    const filterCondition: FilterCondition | undefined =
      selectedGroupIds.current.size > 0
        ? {
            operator: inAll.current ? FilterOperator.IN_ALL : FilterOperator.IN_ANY,
            value: Array.from(selectedGroupIds.current),
          }
        : undefined;
    updateFilterModel({ [fieldName]: filterCondition });
    analyticIssuer?.clicked(["Apply"]);
  }, [analyticIssuer, fieldName, inAll, updateFilterModel, selectedGroupIds]);

  const handleResetClick = useCallback(() => {
    updateFilterModel({ [fieldName]: undefined });
    analyticIssuer?.clicked(["Reset"]);
  }, [analyticIssuer, fieldName, updateFilterModel]);

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        {intl.formatMessage(
          {
            id: "grid.groupFilterPanel.title",
            defaultMessage:
              "Show {entityName} that belong to {inAll, select, true {all} other {any}} of these groups",
            description: "Title in GridFilter tool panel",
          },
          {
            entityName: getEntityTypeDisplayName(intl, entityType, {
              lowercase: true,
              plural: true,
            }),
            inAll: inAll.current,
          }
        )}
      </div>
      <div className={styles.header}>
        <Radio.Group
          name="groupFilterStrategy"
          onChange={handleFilterStrategyChange}
          size="small"
          value={inAll.current}
        >
          <Radio value={true}>
            {intl.formatMessage({
              id: "grid.groupFilterPanel.strategy.inAll",
              defaultMessage: "Records with all groups",
              description: "Filtering strategy name in GridFilter tool panel",
            })}
          </Radio>
          <Radio value={false}>
            {intl.formatMessage({
              id: "grid.groupFilterPanel.strategy.inAny",
              defaultMessage: "Records with any groups",
              description: "Filtering strategy name in GridFilter tool panel",
            })}
          </Radio>
        </Radio.Group>
      </div>
      <div className={styles.groupList}>
        <Select<Array<Group["id"]>>
          className={styles.select}
          filterOption={defaultFilterOption}
          mode="multiple"
          onChange={handleGroupSelectionChange}
          options={groupOptions}
          placeholder={intl.formatMessage({
            id: "grid.groupFilterPanel.select.placeholder",
            defaultMessage: "Click or type to select groups",
            description: "Placeholder displayed in a GridFilter tool panel's select field",
          })}
          size="small"
          value={selectValues}
        />
      </div>
      <div className={styles.footer}>
        <Button onClick={handleResetClick} type="default">
          {intl.formatMessage({
            id: "grid.groupFilterPanel.reset",
            defaultMessage: "Reset",
            description: "Reset button title in GridFilter tool panel",
          })}
        </Button>
        <Button onClick={handleApplyClick} type="primary">
          {intl.formatMessage({
            id: "grid.groupFilterPanel.apply",
            defaultMessage: "Apply",
            description: "Apply button title in GridFilter tool panel",
          })}
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = (
  state: RootState,
  ownProps: { entityType: EntityTypeSupportingGroups }
) => ({
  groups: getGroupsForEntity(state)(ownProps.entityType),
});

const ConnectedGroupsFilterPanel = connect(mapStateToProps)(GroupsFilterPanel);

const buildCustomGroupFilter =
  (entityType: EntityTypeSupportingGroups, analyticIssuer?: AnalyticsService) =>
  (props: Omit<Props, "analyticIssuer" | "entityType">) =>
    (
      <ConnectedGroupsFilterPanel
        analyticIssuer={analyticIssuer}
        entityType={entityType}
        {...props}
      />
    );

export const CompanyGroupsFilterPanel = buildCustomGroupFilter(
  EntityType.COMPANY,
  companyTableAnalytics.extend(["Side Filter", "Groups"])
);
export const PeopleGroupsFilterPanel = buildCustomGroupFilter(
  EntityType.PERSON,
  peopleTableAnalytics.extend(["Side Filter", "Groups"])
);
export const DealGroupsFilterPanel = buildCustomGroupFilter(EntityType.DEAL);

export default ConnectedGroupsFilterPanel;
